import { getEnvironmentLabel } from './host'

/**
 * The single place that decides whether this deployment carries a visual
 * environment mark and what it looks like. Consumed by the `EnvBar` at the top
 * of the app, the router's title guard, and `applyEnvFavicon()` below, so they
 * always agree.
 *
 * Prod deliberately has no mark: real lab users look at that UI. The resulting
 * rule — "no mark means prod" — also fails in the safe direction, since a mark
 * that stops rendering makes you treat the environment as prod rather than the
 * other way round. An unresolved environment is treated the same way.
 *
 * 'local' maps onto 'dev' on purpose: localhost resolves every environment
 * value against `lab-services-dev` (see `apps-config.ts` in lab-cloud), so
 * colors, favicon and title prefix all stay the same as 'dev'. The bar's
 * `barLabel` text is the one exception — it appends "- LOCALHOST" so a
 * developer can tell at a glance whether they're on their own machine or
 * looking at the shared dev.app.milvalab.com deployment.
 */
export interface IEnvIndicator {
  /**
   * Short uppercase environment name, e.g. 'DEV'. Used for the `document.title`
   * prefix, where the full word would eat the space that matters most — a tab
   * strip truncates, and the page name has to survive that.
   */
  label: string
  /** Full environment name shown in the bar's badge, where there is room. */
  barLabel: string
  /** Solid background color of the 18px environment bar. */
  barBgColor: string
  /** Badge background — pale, so it separates from the bar behind it. */
  badgeBgColor: string
  /** Badge text color. */
  badgeTextColor: string
  /**
   * Recolored Milva Lab mark for this environment, served from `public/`.
   * Same artwork as the prod `favicon.png` — only the palette differs, and
   * the white background and LAB lettering are knocked out to transparency
   * in all three, so the icon reads on a light or a dark tab strip.
   */
  faviconPath: string
}

// Literal colors rather than design tokens: the bar has to look identical in
// lab-management and lab-admin, and the two apps' own feedback tokens are too
// far apart to carry that (lab-admin's --color-warning is #e2c802 on #fff8cf,
// which fails contrast outright).
//
// The bar takes the lighter end of its hue and the badge the pale end, so the
// badge still separates from the bar without a border and the text still
// clears 4.5:1.
const ENV_INDICATORS = {
  dev: {
    label: 'DEV',
    barLabel: 'DEVELOPMENT',
    barBgColor: '#2b4cb8',
    badgeBgColor: '#dbeafe',
    badgeTextColor: '#1e40af',
    faviconPath: '/favicon-dev.png',
  },
  stage: {
    label: 'STAGE',
    barLabel: 'STAGE',
    barBgColor: '#c05f11',
    badgeBgColor: '#fef3c7',
    badgeTextColor: '#92400e',
    faviconPath: '/favicon-stage.png',
  },
} as const satisfies Record<string, IEnvIndicator>

/** The mark for this deployment, or `null` on prod and on an unresolved environment. */
export function getEnvIndicator(): IEnvIndicator | null {
  const environmentLabel = getEnvironmentLabel()

  if (environmentLabel === 'local') {
    return { ...ENV_INDICATORS.dev, barLabel: 'DEVELOPMENT - LOCALHOST' }
  }

  if (environmentLabel === 'dev') {
    return ENV_INDICATORS.dev
  }

  if (environmentLabel === 'stage') return ENV_INDICATORS.stage

  return null
}

/**
 * Height of the bar, in pixels. Single source for both the bar's own height and
 * the CSS variable below, so the two cannot drift apart.
 */
export const ENV_BAR_HEIGHT_PX = 18

/**
 * Publish the bar's height as `--env-bar-height` on the document root, so that
 * everything measuring itself against the viewport can subtract it and the app
 * behaves as if the window were simply that many pixels shorter.
 *
 * Set on the root rather than on the shell because the things that need it are
 * exactly the ones that escape the shell: modal overlays and the action search
 * are teleported to <body>, and `position: fixed` resolves against the viewport
 * regardless of where it sits in the tree.
 *
 * Prod returns early and the stylesheet's 0px default stands, so no layout in
 * production is computed through this at all. Call once during bootstrap, next
 * to applyEnvFavicon().
 *
 * The bar does not read this variable for its own height — it sizes itself from
 * ENV_BAR_HEIGHT_PX directly, so a missing call here leaves the bar visible and
 * the surrounding layout wrong, rather than silently collapsing it to nothing.
 */
export function applyEnvBarHeight(): void {
  if (!getEnvIndicator()) return

  document.documentElement.style.setProperty(
    '--env-bar-height',
    `${ENV_BAR_HEIGHT_PX}px`
  )
}

/** `'[DEV] '` on non-prod, `''` on prod — prefixed onto `document.title`. */
export function getEnvTitlePrefix(): string {
  const indicator = getEnvIndicator()

  return indicator ? `[${indicator.label}] ` : ''
}

/**
 * Swap the tab icon for the recolored Milva Lab mark on non-prod; prod keeps the
 * `favicon.png` index.html already points at. Call once during bootstrap, after
 * initFirebase() has resolved the project ID.
 *
 * Mutates the existing <link rel="icon"> instead of appending a second one —
 * with two icons present, which one a browser picks is not ours to decide.
 */
export function applyEnvFavicon(): void {
  const indicator = getEnvIndicator()
  if (!indicator) return

  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) return

  link.type = 'image/png'
  link.href = indicator.faviconPath
}
